// ADR 16.9.26r §2 — the dock's state, pure.
//
// Three values, not two: `detached` is the dock living in another window
// (§3), and it is the one that is **never persisted** — a reload cannot know
// whether the other window still exists, so a stored `detached` would be a
// dock that is nowhere. The next pop-out re-derives it: the named
// `window.open` reuses the window, and its fresh mount posts `hello`.
//
// `bye` restores the state that preceded `hello`, so the memory carries both
// the current value and the one to fall back to. A `bye` heard while not
// `detached` (a reloaded tab hearing an old popup close) is a no-op — there
// is no prior state to restore.

import type { TutorScreen } from '../../../wasm'

export type DockState = 'closed' | 'open' | 'detached'

export interface DockMemory {
  state: DockState
  /** What `bye` returns to — meaningful only while `state` is `detached`. */
  prior: Exclude<DockState, 'detached'>
}

/** Same `try/catch` idiom as the sidebar's collapse (`useCollapsedChrome.ts`). */
export const DOCK_STORAGE_KEY = 'lingara.tutor.dock'

const CLOSED: DockMemory = { state: 'closed', prior: 'closed' }

export function readDockState(): DockMemory {
  try {
    const stored = window.localStorage.getItem(DOCK_STORAGE_KEY)
    return stored === 'open' ? { state: 'open', prior: 'open' } : CLOSED
  } catch {
    return CLOSED
  }
}

/** Writes `open` / `closed`; `detached` leaves the store as it was. */
export function writeDockState(memory: DockMemory): void {
  if (memory.state === 'detached') return
  try {
    window.localStorage.setItem(DOCK_STORAGE_KEY, memory.state)
  } catch {
    // The state still holds for this session.
  }
}

export function opened(memory: DockMemory): DockMemory {
  return memory.state === 'detached' ? memory : { state: 'open', prior: 'open' }
}

export function closed(memory: DockMemory): DockMemory {
  return memory.state === 'detached' ? memory : { state: 'closed', prior: 'closed' }
}

/** The popup said hello: whatever the dock was, it is now over there. */
export function hello(memory: DockMemory): DockMemory {
  return memory.state === 'detached' ? memory : { state: 'detached', prior: memory.state }
}

/** The popup left: back to what preceded it. */
export function bye(memory: DockMemory): DockMemory {
  return memory.state === 'detached' ? { state: memory.prior, prior: memory.prior } : memory
}

/** The return press found the popup dead: the dock comes home, open. */
export function returned(): DockMemory {
  return { state: 'open', prior: 'open' }
}

/**
 * The FAB routes → the `TutorScreen` they report. `null` is every other route:
 * no FAB. ADR 28.9.26e §9 — Android's two, `TUTOR_FAB_ROUTES` exactly:
 * `/plans/:id` and `/vocab`. `/plans/new` is the redirect to Home and
 * `/plans/:id/play` the Walkthrough.
 */
export function fabScreen(pathname: string): TutorScreen | null {
  if (/^\/plans\/(?!new(?:\/|$))[^/]+\/?$/.test(pathname)) return 'lesson_plan_detail'
  if (/^\/vocab\/?$/.test(pathname)) return 'vocab'
  return null
}

/**
 * Where an open dock may be drawn → the `TutorScreen` the dock reports as
 * `surface`. `null` is every other route: no in-page dock. ADR 29.9.26af §2 —
 * the FAB routes plus `/flashcards`, where the coach card opens the dock
 * seeded, as both phones' sheets render on any route. `/flashcards/review`
 * stays out: 28.9.26e §8 made it immersive, and it has no coach card.
 */
export function dockScreen(pathname: string): TutorScreen | null {
  if (/^\/flashcards\/?$/.test(pathname)) return 'flashcards'
  return fabScreen(pathname)
}
